import type { ErrorEvent } from "@sentry/nextjs";

// Page translators (Chrome/Google Translate, Edge's Microsoft Translator, Safari, Firefox) rewrite text nodes before or
// during hydration; React then reports a mismatch (#418/#423/#425) or trips over a node it no longer owns. The site is
// English-only, so a clash is dropped when the browser locale is not English (React's client re-render has usually
// wiped the translator's marks by the time the event is sent) or the page still carries the marks. Real mismatches
// keep reporting from English browsers.
const TRANSLATED =
  "[_msttexthash], [_msthash], html.translated-ltr, html.translated-rtl";
const BOT = /bot|spider|crawl|slurp|headless/i;
const CHUNK_LOAD = /Failed to load chunk|ChunkLoadError|Loading chunk \S+ failed/;
const DOM_CLASH =
  /Minified React error #(418|423|425)\b|Hydration failed|Failed to execute '(removeChild|insertBefore)' on 'Node'/;

/** The extension executor can be rewritten to app:/// by Sentry's Next.js integration. */
export function filterBrowserError(
  event: ErrorEvent,
  doc: Pick<Document, "querySelector"> | undefined = globalThis.document,
): ErrorEvent | null {
  const exceptions = event.exception?.values;
  if (!exceptions?.length) return event;

  const isExtensionError = exceptions.every((exception) => {
    const frames = exception.stacktrace?.frames;
    return (
      exception.type === "TypeError" &&
      exception.value === "Cannot read properties of undefined (reading 'M_ID')" &&
      !!frames?.length &&
      frames.every((frame) =>
        /^(?:app:\/\/\/|chrome-extension:\/\/[^/]+\/)executors\/200\.js$/.test(
          frame.filename ?? "",
        ),
      )
    );
  });
  if (isExtensionError) return null;

  const values = exceptions.map((exception) => exception.value ?? "");
  const userAgent = event.request?.headers?.["User-Agent"] ?? "";
  // Crawlers (e.g. Baiduspider-render) render with a cut-off network budget and their own DOM tweaks, so their chunk
  // loads fail and hydration clashes; no reader sees either.
  if (BOT.test(userAgent) && values.some((value) => CHUNK_LOAD.test(value) || DOM_CLASH.test(value))) return null;

  const locale = String(event.contexts?.culture?.locale ?? "en");
  const isTranslatorClash =
    values.some((value) => DOM_CLASH.test(value)) &&
    (!/^en\b/i.test(locale) || doc?.querySelector(TRANSLATED) != null);

  return isTranslatorClash ? null : event;
}
